// Leaving /purchases/claim without finishing is the one action on the page that
// costs the buyer money: Stripe has already charged them, but the subscription
// stays dormant until an account exists to attach it to. Recovery is possible
// (the sweeper emails a claim link) but slow, and support picks up the rest —
// so an accidental tab close is worth one confirmation prompt.
//
// The prompt's wording is NOT ours to choose: every current browser ignores the
// string and shows its own generic "Leave site?" dialog. All we control is
// whether it appears at all, which makes the decision below the whole feature.

export interface ClaimUnloadState {
  /** The row is still claimable — i.e. there is genuinely work left to lose. */
  claimAvailable: boolean;
  /** Already claimed; this render is a confirmation, nothing to protect. */
  claimAlreadyDone: boolean;
  /**
   * We started this navigation on the buyer's behalf — the post-activation
   * redirect, a reload after a failed finalize, or the hand-off to Google/Apple.
   * Prompting here would interrupt the very flow that completes the claim.
   */
  leavingIntentionally: boolean;
}

export function shouldBlockUnload(state: ClaimUnloadState): boolean {
  if (state.leavingIntentionally) return false;
  if (state.claimAlreadyDone) return false;
  return state.claimAvailable;
}

let leavingIntentionally = false;

/**
 * Disarm the guard for a navigation we are about to perform ourselves. Call it
 * immediately before assigning location / handing off to an OAuth redirect.
 */
export function allowClaimNavigation(): void {
  leavingIntentionally = true;
}

/** Test seam — the module-level latch is one-way in the browser by design. */
export function resetClaimNavigationLatch(): void {
  leavingIntentionally = false;
}

type StateSource = () => Omit<ClaimUnloadState, 'leavingIntentionally'>;

/** Returns an uninstall function. */
export function installClaimUnloadGuard(getState: StateSource): () => void {
  const handler = (event: BeforeUnloadEvent) => {
    if (!shouldBlockUnload({ ...getState(), leavingIntentionally })) return undefined;

    // preventDefault is the spec-compliant trigger; returnValue is what older
    // WebKit/Chrome still key off. Both are needed for full coverage, and the
    // returned string is only read by legacy engines.
    event.preventDefault();
    event.returnValue = '';
    return '';
  };

  window.addEventListener('beforeunload', handler);
  return () => window.removeEventListener('beforeunload', handler);
}
